import { BufferWriter, Writer } from "protobufjs";
import {
  MultiSkillTagConvertResponse,
  MultiSkillTagConvertResponseList,
  OccupationSkillTagConvertResponse,
  OccupationSkillTagConvertResponseList,
  SingleSkillTagConvertResponse,
  SingleSkillTagConvertResponseList,
} from "src/infra/api/service/skill_tag_api_convert";
import {
  MultiSkillTag,
  OccupationSkillTag,
  SingleSkillTag,
} from "../../../domain/model/skill_tag";
import {
  IMultiSkillTagRepository,
  IOccupationSkillTagRepository,
  ISingleSkillTagRepository,
  TOccupationGetAllRequest,
} from "../../../domain/repository/skill_tag_repository";
import { IApiClient } from "../client";
import { common, entity } from "../rpc/api";
import { getToken } from "./bind_token";

import SingleSkillTagList = entity.SingleSkillTagList;
import MultiSkillTagList = entity.MultiSkillTagList;
import OccupationSkillTagList = entity.OccupationSkillTagList;
import OccupationSkillTagIDWithPager = entity.OccupationSkillTagIDWithPager;

import Pager = common.Pager;

class SingleSkillTagAPI implements ISingleSkillTagRepository {
  constructor(readonly apiClient: IApiClient) {}

  public getAll = (page: number, offset: number): Promise<SingleSkillTag[]> => {
    return new Promise<SingleSkillTag[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.SingleSkillTagService/GetAll",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: SingleSkillTagList = SingleSkillTagList.decode(binary);
          // BE=>FE 型変換
          const resConverted: SingleSkillTagConvertResponseList =
            SingleSkillTagConvertResponseList.from(res);
          const items: SingleSkillTag[] = resConverted.items.map(
            (item: SingleSkillTagConvertResponse): SingleSkillTag => {
              return SingleSkillTag.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getAllByCategory = (
    category: entity.SingleSkillTag.Category
  ): Promise<SingleSkillTag[]> => {
    return new Promise<SingleSkillTag[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.SingleSkillTagCategory =
            new entity.SingleSkillTagCategory();
          req.category = category;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.SingleSkillTagService/GetAllByCategory",
            token,
            entity.SingleSkillTagCategory.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: SingleSkillTagList = SingleSkillTagList.decode(binary);
          // BE=>FE 型変換
          const resConverted: SingleSkillTagConvertResponseList =
            SingleSkillTagConvertResponseList.from(res);
          const items: SingleSkillTag[] = resConverted.items.map(
            (item: SingleSkillTagConvertResponse): SingleSkillTag => {
              return SingleSkillTag.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };
}

class MultiSkillTagAPI implements IMultiSkillTagRepository {
  constructor(readonly apiClient: IApiClient) {}

  public getAll = (page: number, offset: number): Promise<MultiSkillTag[]> => {
    return new Promise<MultiSkillTag[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.MultiSkillTagService/GetAll",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: MultiSkillTagList = MultiSkillTagList.decode(binary);
          // BE=>FE 型変換
          const resConverted: MultiSkillTagConvertResponseList =
            MultiSkillTagConvertResponseList.from(res);
          const items: MultiSkillTag[] = resConverted.items.map(
            (item: MultiSkillTagConvertResponse): MultiSkillTag => {
              return MultiSkillTag.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getAllByCategory = (
    category: entity.MultiSkillTag.Category
  ): Promise<MultiSkillTag[]> => {
    return new Promise<MultiSkillTag[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.MultiSkillTagCategory =
            new entity.MultiSkillTagCategory();
          req.category = category;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.MultiSkillTagService/GetAllByCategory",
            token,
            entity.MultiSkillTagCategory.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: MultiSkillTagList = MultiSkillTagList.decode(binary);
          // BE=>FE 型変換
          const resConverted: MultiSkillTagConvertResponseList =
            MultiSkillTagConvertResponseList.from(res);
          const items: MultiSkillTag[] = resConverted.items.map(
            (item: MultiSkillTagConvertResponse): MultiSkillTag => {
              return MultiSkillTag.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public get = (id: string): Promise<MultiSkillTag> => {
    return new Promise<MultiSkillTag>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.MultiSkillTagID = new entity.MultiSkillTagID();
          req.id = id;

          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.MultiSkillTagService/Get",
            token,
            entity.MultiSkillTagID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.MultiSkillTag = entity.MultiSkillTag.decode(binary);
          const resConverted: MultiSkillTagConvertResponse =
            MultiSkillTagConvertResponse.from(res);
          resolve(MultiSkillTag.from(resConverted));
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };
}

class OccupationSkillTagAPI implements IOccupationSkillTagRepository {
  constructor(readonly apiClient: IApiClient) {}

  public getAll = (
    occupationGetAllRequest: TOccupationGetAllRequest
  ): Promise<OccupationSkillTag[]> => {
    return new Promise<OccupationSkillTag[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: OccupationSkillTagIDWithPager =
            new OccupationSkillTagIDWithPager();
          req.id = occupationGetAllRequest.id;
          req.pager = occupationGetAllRequest.pager;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.OccupationSkillTagService/GetAll",
            token,
            OccupationSkillTagIDWithPager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: OccupationSkillTagList =
            OccupationSkillTagList.decode(binary);
          // BE=>FE 型変換
          const resConverted: OccupationSkillTagConvertResponseList =
            OccupationSkillTagConvertResponseList.from(res);
          const items: OccupationSkillTag[] = resConverted.items.map(
            (item: OccupationSkillTagConvertResponse): OccupationSkillTag => {
              return OccupationSkillTag.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getAllByCategory = (
    category: entity.OccupationSkillTag.Category
  ): Promise<OccupationSkillTag[]> => {
    return new Promise<OccupationSkillTag[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.OccupationSkillTagCategory =
            new entity.OccupationSkillTagCategory();
          req.category = category;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.OccupationSkillTagService/GetAllByCategory",
            token,
            entity.OccupationSkillTagCategory.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: OccupationSkillTagList =
            OccupationSkillTagList.decode(binary);
          // BE=>FE 型変換
          const resConverted: OccupationSkillTagConvertResponseList =
            OccupationSkillTagConvertResponseList.from(res);
          const items: OccupationSkillTag[] = resConverted.items.map(
            (item: OccupationSkillTagConvertResponse): OccupationSkillTag => {
              return OccupationSkillTag.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getAllCategory = (): Promise<entity.IOccupationSkillTagCategory[]> => {
    return new Promise<entity.IOccupationSkillTagCategory[]>(
      (resolve, reject) => {
        getToken()
          .then((token: string): Promise<Uint8Array> => {
            const req: common.Empty = new common.Empty();
            const writer: BufferWriter | Writer = Writer.create();
            return this.apiClient.postWithToken(
              "/api.OccupationSkillTagService/GetAllCategory",
              token,
              common.Empty.encode(req, writer).finish()
            );
          })
          .then((binary: Uint8Array): void => {
            const res: entity.IOccupationSkillTagCategoryList =
              entity.OccupationSkillTagCategoryList.decode(binary);
            resolve(res.items!);
          })
          .catch((error: Error): void => {
            reject(error);
          });
      }
    );
  };
}

const createSingleSkillTagAPI = (
  apiClient: IApiClient
): ISingleSkillTagRepository => {
  return new SingleSkillTagAPI(apiClient);
};

const createMultiSkillTagAPI = (
  apiClient: IApiClient
): IMultiSkillTagRepository => {
  return new MultiSkillTagAPI(apiClient);
};

const createOccupationSkillTagAPI = (
  apiClient: IApiClient
): IOccupationSkillTagRepository => {
  return new OccupationSkillTagAPI(apiClient);
};

export {
  createSingleSkillTagAPI,
  createMultiSkillTagAPI,
  createOccupationSkillTagAPI,
};



// WEBPACK FOOTER //
// ./src/infra/api/service/skill_tag_api.ts